/** Strukturierter Web-Recherche-Nachweis (n8n-Gate → Portal). Nicht LEX/JUD. */

export interface WebSearchProofSource {
  title: string;
  url: string;
  snippet?: string;
}

export interface WebSearchContact {
  name?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  maps_url?: string;
  source_url: string;
  source_title?: string;
  fallback?: boolean;
}

export interface WebSearchProof {
  status: 'ok' | 'no_match' | 'stub_no_key' | 'rejected_query' | 'http_error';
  audit_id: string;
  web_stamp: string;
  card?: string;
  card_tokens?: number;
  sources: WebSearchProofSource[];
  contact?: WebSearchContact;
}

export const PORTAL_WEB_SEARCH_MARKER = 'portal-web-search:gate-v1';

function text(value: unknown): string {
  return String(value ?? '').trim();
}

/** Sichtbarer Web-Text: Tags und Entities weg, Prozent-Encoding nur im href. */
export function plainWebDisplayText(value: string): string {
  let t = String(value ?? '');
  for (let i = 0; i < 2; i += 1) {
    t = t.replace(/<[^>]*>/g, ' ');
    t = t
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&quot;/gi, '"')
      .replace(/&#0*39;|&apos;/gi, "'")
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>');
    t = t.replace(/&#(\d+);/g, (_, n) => {
      const c = Number(n);
      return Number.isFinite(c) && c > 0 && c < 0x110000 ? String.fromCodePoint(c) : '';
    });
    t = t.replace(/&#x([0-9a-f]+);/gi, (_, h) => {
      const c = parseInt(h, 16);
      return Number.isFinite(c) && c > 0 && c < 0x110000 ? String.fromCodePoint(c) : '';
    });
  }
  t = t.replace(/<[^>]*>/g, ' ').replace(/[<>]/g, '');
  if (/%[0-9A-Fa-f]{2}/.test(t)) {
    try {
      t = decodeURIComponent(t);
    } catch {
      /* keine gültige Folge */
    }
  }
  return t.replace(/[<>]/g, '')
    .replace(/\[([^\[\]]*)\]/g, (_m, inner) => {
      if (/[\u0250-\u02FF\u1D00-\u1DBF\uA700-\uA71F]/.test(inner)) return ' ';
      const letters = String(inner).replace(/[^A-Za-zÄÖÜäöüß]/g, '');
      const other = String(inner).replace(/[A-Za-zÄÖÜäöüß\s]/g, '');
      return other.length > letters.length ? ' ' : _m;
    })
    .replace(/[\u0250-\u02FF\u1D00-\u1DBF\uA700-\uA71F]/g, '')
    .replace(/\(\*\s*[^)]*(?:\)|$)/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function webUrlLabel(url: string): string {
  const raw = String(url || '').trim();
  try {
    return decodeURI(raw);
  } catch {
    return raw;
  }
}

function parseContact(raw: unknown): WebSearchContact | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const o = raw as Record<string, unknown>;
  const sourceUrl = text(o.source_url);
  if (!sourceUrl.startsWith('https://')) return undefined;
  if (/localhost|127\.|\/Mandanten\/|nextcloud/i.test(sourceUrl)) return undefined;
  const address = plainWebDisplayText(text(o.address)).slice(0, 160);
  const phone = plainWebDisplayText(text(o.phone)).slice(0, 40);
  const email = plainWebDisplayText(text(o.email)).slice(0, 80);
  const website = text(o.website);
  const site = website.startsWith('https://') && !/localhost|127\.|nextcloud/i.test(website) ? website : '';
  const name = plainWebDisplayText(text(o.name)).slice(0, 80);
  const maps = text(o.maps_url);
  const mapsOk = Boolean(address) && /^https:\/\/www\.google\.com\/maps\/search\/\?api=1&query=/.test(maps);
  const hasFact = Boolean(address || phone || email || site);
  return {
    name: name || undefined,
    address: address || undefined,
    phone: phone || undefined,
    email: email || undefined,
    website: site || undefined,
    maps_url: mapsOk ? maps : undefined,
    source_url: sourceUrl,
    source_title: plainWebDisplayText(text(o.source_title || name)).slice(0, 120) || undefined,
    fallback: !hasFact,
  };
}
function parseSource(raw: unknown): WebSearchProofSource | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const title = plainWebDisplayText(text(o.title)).slice(0, 120);
  const url = text(o.url);
  if (!title || !url.startsWith('https://')) return null;
  if (/localhost|127\.|\/Mandanten\/|nextcloud/i.test(url)) return null;
  const snippet = plainWebDisplayText(text(o.snippet)).slice(0, 180);
  return snippet ? { title, url, snippet } : { title, url };
}

export function parseWebSearchProof(raw: unknown): WebSearchProof | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const status = text(o.status);
  const allowed = ['ok', 'no_match', 'stub_no_key', 'rejected_query', 'http_error'];
  if (!allowed.includes(status)) return null;
  const auditId = text(o.audit_id);
  const stamp = text(o.web_stamp);
  if (!/^sha256:[0-9a-f]{64}$/i.test(auditId)) return null;
  if (!/^\[WEB:[0-9A-F]{16}\]$/.test(stamp)) return null;
  const sources = (Array.isArray(o.sources) ? o.sources : [])
    .map(parseSource)
    .filter((item): item is WebSearchProofSource => item !== null)
    .slice(0, 5);
  const cardTokens = Number(o.card_tokens);
  const contact = parseContact(o.contact);
  return {
    status: status as WebSearchProof['status'],
    audit_id: auditId,
    web_stamp: stamp,
    card: text(o.card).slice(0, 2000) || undefined,
    card_tokens: Number.isFinite(cardTokens) ? cardTokens : undefined,
    sources,
    ...(contact ? { contact } : {}),
  };
}

export function hasWebSearchProofData(proof?: WebSearchProof | null): boolean {
  return parseWebSearchProof(proof ?? null) !== null;
}

export function stripWebStampForDisplay(text: string): string {
  return String(text || '')
    .replace(/\s*\[WEB:[0-9A-F]{16}\]\s*/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
